import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useBackRoute } from '../hooks/useBackRoute';
import { useAuth } from '../contexts/AuthContext';
import { toValidObjectIdString } from '../utils/security';
import { fetchIadeFaturalari, fetchIadeFaturaDetay } from '../services/iadeFaturaService';
import IadeFaturaDetayModal from '../components/IadeFaturaDetayModal';
import IadeOlusturModal from '../components/IadeOlusturModal';
import TableListFooter from '../components/TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama } from '../utils/pagination';

const TABS = [
  { id: 'onay-bekleyen', label: 'Onay Bekleyen', tab: 'onay-bekleyen' },
  { id: 'fatura-bekleyen', label: 'Fatura Bekleyen', tab: 'fatura-bekleyen' },
  { id: 'tamamlanan', label: 'Tamamlanan', tab: 'tamamlanan' },
];

const TAB_IDS = TABS.map((t) => t.id);

const DURUM_LABELS = {
  OnayBekliyor: 'Onay Bekliyor',
  FaturaBekleyen: 'Fatura Bekleyen',
  Tamamlandi: 'Tamamlandı',
  IptalEdildi: 'İptal Edildi',
};

const getId = (o) => toValidObjectIdString(o?.id ?? o?._id) || '';

const formatCurrency = (amount) => {
  if (amount == null) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

const formatApiDate = (d) => {
  if (!d) return '—';
  try {
    const date = typeof d === 'string' ? new Date(d) : d;
    return date.toLocaleDateString('tr-TR');
  } catch {
    return '—';
  }
};

const getErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  return err?.message || err?.data?.mesaj || err?.data?.message || fallback;
};

const IadeFaturalariPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlInitRef = useRef(false);
  const tabFromUrl = searchParams.get('tab');
  const initialTab = TAB_IDS.includes(tabFromUrl) ? tabFromUrl : 'onay-bekleyen';
  const aramaFromUrl = searchParams.get('arama') || '';

  const goBack = useBackRoute('/');
  const { user } = useAuth();
  const isTechService = ['tech-service', 'teknikservis', 'techservice'].includes(
    (user?.rol || '').toLowerCase()
  );
  const [list, setList] = useState([]);
  const [iadeOlusturOpen, setIadeOlusturOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });

  const tabConfig = useMemo(() => TABS.find((t) => t.id === activeTab) || TABS[0], [activeTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  useEffect(() => {
    if (urlInitRef.current) return;
    urlInitRef.current = true;
    if (!tabFromUrl || !TAB_IDS.includes(tabFromUrl)) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', initialTab);
        if (aramaFromUrl) next.set('arama', aramaFromUrl);
        return next;
      }, { replace: true });
    }
  }, [tabFromUrl, initialTab, aramaFromUrl, setSearchParams]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchIadeFaturalari({
        sayfa: currentPage,
        limit: PAGE_DEFAULT_LIMIT,
        tab: tabConfig.tab,
        arama: aramaFromUrl || undefined,
      });
      if (res?.basarili && res?.veri) {
        setList(res.veri.iadeFaturalari || []);
        setPaginationMeta(normalizeSayfalama(res.veri.sayfalama));
      } else {
        setList([]);
        setPaginationMeta({ total: 0, totalPages: 1 });
      }
    } catch (err) {
      setError(getErrorMessage(err, 'İade faturaları yüklenemedi'));
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [tabConfig, currentPage, aramaFromUrl]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const id = location.state?.openIadeFaturaId;
    if (!id) return;
    let cancelled = false;
    const open = async () => {
      try {
        const res = await fetchIadeFaturaDetay(id);
        if (!cancelled && res?.basarili && res?.veri?.iadeFatura) {
          setSelected(res.veri.iadeFatura);
          setDetailOpen(true);
        }
      } catch (err) {
        if (!cancelled) window.showNotification?.('error', getErrorMessage(err, 'İade fatura detayı yüklenemedi'));
      } finally {
        if (!cancelled) navigate(location.pathname, { replace: true, state: {} });
      }
    };
    open();
    return () => { cancelled = true; };
  }, [location.state?.openIadeFaturaId, location.pathname, navigate]);

  const handleViewDetail = async (item) => {
    const id = getId(item);
    if (!id) return;
    try {
      const res = await fetchIadeFaturaDetay(id);
      if (res?.basarili && res?.veri?.iadeFatura) {
        setSelected(res.veri.iadeFatura);
        setDetailOpen(true);
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Detay yüklenemedi'));
    }
  };

  return (
    <main className="main-content">
      <div className="content-header">
        <div>
          <button
            type="button"
            className="secondary-btn"
            onClick={goBack}
            style={{ marginBottom: '16px' }}
          >
            <i className="fas fa-arrow-left"></i>
            <span>Geri Dön</span>
          </button>
          <h1>İade Faturaları</h1>
        </div>
        {isTechService && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setIadeOlusturOpen(true)}
          >
            <i className="fas fa-plus" style={{ marginRight: 8 }}></i>
            İade Oluştur
          </button>
        )}
      </div>

      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.id);
              setCurrentPage(1);
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('tab', tab.id);
                return next;
              }, { replace: true });
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Yükleniyor</div>
          </div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : list.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-gray)' }}>
            İade fatura bulunamadı
          </div>
        ) : (
          <>
          <div className="table-container">
            <table className="admins-table">
              <thead>
                <tr>
                  <th>İade Fatura No</th>
                  <th>Teknik Servis</th>
                  <th>Tarih</th>
                  <th style={{ textAlign: 'right' }}>Tutar</th>
                  <th>Durum</th>
                  <th className="islemler-sutunu">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {list.map((item) => (
                  <tr key={getId(item)}>
                    <td style={{ fontWeight: 600, fontFamily: 'ui-monospace, monospace' }}>
                      {item.iadeFaturaNumarasi || '—'}
                    </td>
                    <td style={{ fontWeight: 500 }}>{item.teknikServisAdi || '—'}</td>
                    <td>{formatApiDate(item.olusturmaTarihi)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.toplamTutar)}</td>
                    <td>{DURUM_LABELS[item.durum] || item.durum}</td>
                    <td className="islemler-sutunu">
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <button
                          type="button"
                          className="category-action-btn edit-btn"
                          onClick={() => handleViewDetail(item)}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TableListFooter
            totalRecords={paginationMeta.total}
            currentPage={currentPage}
            totalPages={paginationMeta.totalPages || 1}
            onPageChange={setCurrentPage}
            loading={loading}
            embedded
          />
          </>
        )}
      </div>

      {detailOpen && selected && (
        <IadeFaturaDetayModal
          iadeFatura={selected}
          onClose={() => {
            setDetailOpen(false);
            setSelected(null);
          }}
          onRefresh={loadList}
        />
      )}

      {iadeOlusturOpen && (
        <IadeOlusturModal
          onClose={() => setIadeOlusturOpen(false)}
          onSuccess={loadList}
        />
      )}
    </main>
  );
};

export default IadeFaturalariPage;
